import React, { useState, useEffect, useCallback } from "react";
import { ChevronUp, Loader2, Building2, UserPlus, X, Check, User, UserX, UserCheck } from "lucide-react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import CompanyAccordion from "./components/CompanyAccordion";
import RoleSectionTable from "./components/RoleSectionTable";
import ErrorState from "./components/ErrorState";
import GlobalErrorState from "./components/GlobalErrorState";
import HActionButton from "../../../hcomponents/HActionButton";
import HSpinner from "../../../hcomponents/HSpinner";

import Pagination from "../../../components/Pagination";
import DeleteUserModal from "./components/modals/DeleteUserModal";
import EditUserRoleModal from "./components/modals/EditUserRoleModal";
import EditOrganizationModal from "./components/modals/EditOrganizationModal";
import DeleteOrganizationModal from "./components/modals/DeleteOrganizationModal";
import { Dialog, DialogContent, DialogTitle } from "../../../shadcn/ui/dialog";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import HBadge1 from "../../../hcomponents/HBadge1";
import { Badge } from "../../../shadcn/ui/badge";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import HUpgradeButton from "../../../hcomponents/HUpgradeButton";

// Hooks
import { useHostClientsData } from "./hooks/useHostClientsData";
import { useUserManagement } from "./hooks/useUserManagement";
import { useOrganizationManagement } from "./hooks/useOrganizationManagement";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import { useRoleSections } from "./hooks/useRoleSections";

// Utils
import { getFilteredCompanies } from "./utils/hostClientsUtils";
import { showApiMessage } from "../../../utils/messageHandler";
import { apiV2Service, organizationApiService } from "../../../services/apiV2Service";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import logger from "../../../utils/logger";
import * as localService from "../../../services/localService";
import toastUtils from "../../../utils/toast";
import { RoleConstants } from "../../../utils/constants";

// Config
import { employeeColumns, createActionsColumn } from "./config/tableColumns";

const HostClients = () => {
  const history = useHistory();
  const featureFlags = useSelector(state => state.subscription?.featureFlags || {});
  const hasCustomerManagement = !!featureFlags?.CUSTOMER_MANAGEMENT;

  // Get current user role for feature restrictions
  const currentUser = localService.getUser();
  const userRole = currentUser?.organizationRole?.code || currentUser?.role || "";
  const orgTypeCode = currentUser?.organization?.type?.code || currentUser?.organization?.typeCode || "";

  // Role-based feature flags
  const isHostPropertyManagement = orgTypeCode === "HOST_PROPERTY_MANAGEMENT";
  const isRoleSubscriberHostOperations = userRole === "ROLE_SUBSCRIBER_HOST_OPERATIONS";
  const _isRoleHost = userRole === "ROLE_HOST";

  // Use centralized feature access control
  const canManageOrganizations = FeatureAccess.canManageOrganizations(currentUser);

  const goToUpgrade = useCallback(() => {
    // Read user role from localStorage (same as HSidebarPro)
    const userData = localService.getUser();
    const userRoleCode = userData?.organizationRole?.code;

    logger.log("kflkodo [goToUpgrade] User data:", {
      userData,
      userRoleCode,
      allowedRoles: RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES,
    });

    const isAdminRole = RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES.includes(userRoleCode);

    logger.log("kflkodo [goToUpgrade] Role check:", {
      userRoleCode,
      isAdminRole,
      isIncluded: RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES.includes(userRoleCode),
    });

    if (!isAdminRole) {
      logger.log("kflkodo [goToUpgrade] NOT admin role, showing warning");
      toastUtils.warning("Contact Admin", {
        description: "Contact your admin to upgrade your account to access more features.",
      });
      return;
    }

    logger.log("kflkodo [goToUpgrade] IS admin role, navigating to pricing");
    history.push("/host/pricing?view=packages");
  }, [history]);

  const showCustomerManagementUpgrade = useCallback(() => {
    toastUtils.info("Upgrade to manage customers", {
      action: {
        label: "View plans",
        onClick: goToUpgrade,
      },
    });
  }, [goToUpgrade]);

  // Helper to check if a role is a customer role (should be gated) vs staff role (should not be gated)
  const isCustomerRole = useCallback(role => {
    if (!role) return false;
    logger.log("FJNWSM isCustomerRole check:", { roleCode: role.code, roleName: role.name });
    // Staff roles in host's own organization (should NOT be gated)
    const staffRoleCodes = [
      "ROLE_SUBSCRIBER_HOST",
      "ROLE_SUBSCRIBER_HOST_OPERATIONS",
      "ROLE_HOST",
      "ROLE_HOST_OPERATIONS",
    ];
    if (staffRoleCodes.includes(role.code)) {
      logger.log("FJNWSM Role is staff role, NOT gating");
      return false;
    }
    // Customer roles (should be gated) - ONLY these specific roles
    const customerRoleCodes = ["ROLE_HOST_CUSTOMER_INDIVIDUAL", "ROLE_HOST_CUSTOMER_OWNER"];
    if (customerRoleCodes.includes(role.code)) {
      logger.log("FJNWSM Role is customer role, GATING");
      return true;
    }
    // Default: if it's not explicitly a customer role, assume it's a staff role (don't gate it)
    logger.log("FJNWSM Role not in any list, defaulting to NOT gating");
    return false;
  }, []);

  const runWithCustomerManagement = useCallback(
    callback => {
      if (hasCustomerManagement) {
        return typeof callback === "function" ? callback() : undefined;
      }

      showCustomerManagementUpgrade();
      return undefined;
    },
    [hasCustomerManagement, showCustomerManagementUpgrade]
  );

  // Helper to conditionally gate actions based on whether it's customer management
  const runWithCustomerManagementIfNeeded = useCallback(
    (callback, role = null) => {
      logger.log("FJNWSM runWithCustomerManagementIfNeeded", { role: role?.code, hasCustomerManagement });
      if (role && !isCustomerRole(role)) {
        logger.log("FJNWSM Staff role, executing without gating");
        return typeof callback === "function" ? callback() : undefined;
      }
      logger.log("FJNWSM Checking customer management");
      return runWithCustomerManagement(callback);
    },
    [isCustomerRole, runWithCustomerManagement, hasCustomerManagement]
  );

  // Custom hooks for data and business logic
  const {
    openCompanyAccordion,
    setOpenCompanyAccordion,
    companyManagementOpen,
    setCompanyManagementOpen,
    userManagementOpen,
    setUserManagementOpen,
    organizations,
    organizationsByStatus,
    loading,
    error,
    pagination,
    organizationTypes,
    organizationRoles,
    countries,
    isLoadingCountries,
    fetchOrganizations,
    fetchOrganizationTypes,
    fetchOrganizationRoles,
    handleOrganizationPageChange,
  } = useHostClientsData();

  const {
    showInviteModal,
    setShowInviteModal,
    inviteUserForm,
    selectedRole,
    setSelectedRole,
    isBookingLoading,
    showDeleteUserModal,
    setShowDeleteUserModal,
    userToDelete,
    deleteUserLoading,
    showEditUserRoleModal,
    setShowEditUserRoleModal,
    showEditUserDetailsModal,
    setShowEditUserDetailsModal,
    selectedUserForEdit,
    editUserRoleForm,
    editUserDetailsForm,
    setEditUserDetailsForm,
    editUserRoleLoading,
    editUserDetailsLoading,
    editUserError,
    handleEditUserRole,
    handleEditUserRoleInputChange,
    handleEditUserRoleSubmit,
    handleEditUserDetails,
    handleEditUserDetailsInputChange,
    handleEditUserDetailsSubmit,
    handleDeleteUser,
    handleDeleteUserConfirm,
    handleBulkImport: _handleBulkImport,
    handleInviteUserInputChange,
    inviteUser,
    handleResendActivationEmail,
    handleToggleUserStatus,
    // Username validation states for invite user modal
    isCheckingUsername,
    usernameAvailable,
    usernameError,
  } = useUserManagement();

  const {
    isCreatingOrganization: _isCreatingOrganization,
    setIsCreatingOrganization,
    createOrgForm,
    setCreateOrgForm,
    handleCreateOrgInputChange,
    createOrganization,
    addEmployeeToChildOrganization,
    showEditOrganizationModal,
    setShowEditOrganizationModal,
    selectedOrganization,
    editOrganizationForm,
    editOrganizationLoading,
    editOrganizationError,
    fetchingOrganizationData,
    handleEditOrganization,
    handleEditOrganizationInputChange,
    handleEditOrganizationSubmit,
    showDeleteOrganizationModal,
    setShowDeleteOrganizationModal,
    organizationToDelete,
    deleteOrganizationLoading,
    handleDeleteOrganization,
    handleDeleteOrganizationConfirm,
    // Organization validation states
    isCheckingOrganization,
    organizationCodeAvailable: _organizationCodeAvailable,
    organizationNameAvailable,
    organizationCodeError: _organizationCodeError,
    organizationNameError,

    // Username validation states for create organization modal
    isCheckingUsername: isCheckingOrgUsername,
    usernameAvailable: orgUsernameAvailable,
    usernameError: orgUsernameError,
  } = useOrganizationManagement();

  const {
    roleSections,
    availableRoles,
    fetchRoleSectionUsers,
    refreshAllRoleSections,
    updateRoleSectionStatus,
    updateRoleSectionSearch,
    toggleRoleSection,
  } = useRoleSections(organizationTypes, organizationRoles);

  // Initial data fetching
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchOrganizations(), fetchOrganizationTypes(), fetchOrganizationRoles()]);
    };
    loadInitialData();
  }, [fetchOrganizations, fetchOrganizationTypes, fetchOrganizationRoles]);

  // Add Employee Modal State
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [selectedOrganizationForEmployee, setSelectedOrganizationForEmployee] = useState(null);

  // Expanded organization data (fetched when accordion is opened)
  const [expandedOrgData, setExpandedOrgData] = useState({});

  // Status section accordion state
  const [openStatusSections, setOpenStatusSections] = useState({
    ACTIVE: true,
    INACTIVE: true,
    DISABLED: true,
  });
  const [addEmployeeForm, setAddEmployeeForm] = useState({
    username: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    msisdn: "",
    role: "ROLE_EMPLOYEE", // Default role for employees;
  });

  // Add Employee Username validation states
  const [isCheckingAddEmployeeUsername, setIsCheckingAddEmployeeUsername] = useState(false);
  const [addEmployeeUsernameAvailable, setAddEmployeeUsernameAvailable] = useState(null);
  const [addEmployeeUsernameError, setAddEmployeeUsernameError] = useState("");

  // Add Employee Username validation checking with debounce
  const checkAddEmployeeUsernameAvailability = async usernameToCheck => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setAddEmployeeUsernameAvailable(null);
      setAddEmployeeUsernameError("");
      return;
    }

    setIsCheckingAddEmployeeUsername(true);
    setAddEmployeeUsernameError("");

    try {
      const response = await apiV2Service.checkUsernameAvailability(usernameToCheck);
      logger.log("Add Employee Username check response:", response);
      logger.log("Add Employee Response data:", response.data);
      const exists = response.data?.exists || false;
      logger.log("Add Employee Exists value:", exists);

      if (exists) {
        setAddEmployeeUsernameAvailable(false);
        setAddEmployeeUsernameError("Username is already taken");
      } else {
        setAddEmployeeUsernameAvailable(true);
        setAddEmployeeUsernameError("");
      }
    } catch (error) {
      logger.error("Add Employee Username availability check error:", error);
      setAddEmployeeUsernameAvailable(null);
      setAddEmployeeUsernameError("Username is unavailable");
    } finally {
      setIsCheckingAddEmployeeUsername(false);
    }
  };

  // Debounced add employee username validation check
  useEffect(() => {
    // Check for leading/trailing spaces
    if (addEmployeeForm.username.trim() !== addEmployeeForm.username) {
      setAddEmployeeUsernameAvailable(false);
      setAddEmployeeUsernameError("Username cannot have leading or trailing spaces");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkAddEmployeeUsernameAvailability(addEmployeeForm.username);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [addEmployeeForm.username]);

  const getStatusColor = status => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "INACTIVE":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "DISABLED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Separate modal for creating organization
  const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);

  // Create actions column with handlers
  const actionsColumn = createActionsColumn({
    handleEditUserRole: user => {
      handleEditUserRole(user);
    },
    handleEditUserDetails: user => {
      handleEditUserDetails(user);
    },
    handleDeleteUser: user => {
      handleDeleteUser(user);
    },
    handleToggleStatus: async user => {
      const result = await handleToggleUserStatus(user);
      if (result?.success) {
        handleUserOperationSuccess();
      }
    },
  });

  const handleEditOrganizationGuarded = useCallback(
    organization =>
      runWithCustomerManagement(() => handleEditOrganization(organization, expandedOrgData[organization.id])),
    [runWithCustomerManagement, handleEditOrganization, expandedOrgData]
  );

  const handleDeleteOrganizationGuarded = useCallback(
    organization => {
      // Only admin/owner roles may delete organizations.
      // ROLE_SUBSCRIBER_HOST_OPERATIONS (host employees) must be blocked.
      const adminRoles = ["ROLE_SUBSCRIBER_HOST", "ROLE_HOST"];
      if (!adminRoles.includes(userRole)) {
        toastUtils.warning("Not authorized", {
          description: "Only administrators can delete organizations.",
        });
        return;
      }
      runWithCustomerManagement(() => handleDeleteOrganization(organization));
    },
    [runWithCustomerManagement, handleDeleteOrganization, userRole]
  );

  // Combine base columns with actions
  const _tableColumns = [...employeeColumns, actionsColumn];

  // Get filtered data
  const _filteredCompanies = getFilteredCompanies(organizations);

  // Error state logic
  const hasOrganizationError = error && !loading;
  // Only show role sections error if there's an actual error, not permission-related ones
  const hasRoleSectionsErrors = Object.values(roleSections).some(section => section.error);
  const hasRoleSectionsError =
    hasOrganizationError ||
    (!loading && organizationRoles.length === 0 && availableRoles.length === 0 && hasRoleSectionsErrors);
  const allRoleSectionsHaveErrors =
    Object.keys(roleSections).length > 0 && Object.values(roleSections).every(section => section.error);
  const shouldShowGlobalError = hasOrganizationError && allRoleSectionsHaveErrors;

  // Debug logging
  logger.log("HostClients Debug:", {
    loading,
    error,
    hasOrganizationError,
    hasRoleSectionsError,
    organizationRoles: organizationRoles.length,
    availableRoles: availableRoles.length,
    roleSections: Object.keys(roleSections).length,
    shouldShowGlobalError,
  });

  // Handle successful operations
  const handleUserOperationSuccess = () => {
    refreshAllRoleSections();
  };

  const handleEditUserRoleSubmitWithRefresh = async () => {
    const result = await handleEditUserRoleSubmit();
    if (result?.success) {
      handleUserOperationSuccess();
    }
    return result;
  };

  const handleDeleteUserConfirmWithRefresh = async () => {
    const result = await handleDeleteUserConfirm();
    if (result?.success) {
      handleUserOperationSuccess();
    }
    return result;
  };

  const handleEditUserDetailsSubmitWithRefresh = async () => {
    const result = await handleEditUserDetailsSubmit();
    if (result?.success) {
      handleUserOperationSuccess();
    }
    return result;
  };

  const handleInviteUserWithRefresh = async () => {
    logger.log("FJNWSM handleInviteUserWithRefresh, selectedRole:", selectedRole?.code);
    if (selectedRole && !isCustomerRole(selectedRole)) {
      logger.log("FJNWSM Staff role, inviting without gating");
      const result = await inviteUser(organizationRoles);
      if (result?.success) {
        handleUserOperationSuccess();
      }
      return result;
    }
    logger.log("FJNWSM Customer role, checking feature flag");
    return runWithCustomerManagement(async () => {
      const result = await inviteUser(organizationRoles);
      if (result?.success) {
        handleUserOperationSuccess();
      }
      return result;
    });
  };

  // Friendly role label for headers
  const getFriendlyRoleName = roleName => {
    const ln = (roleName || "").trim().toLowerCase();
    if (ln === "prop management individual subscriber" || ln === "property management individual subscriber") {
      // Use centralized label logic
      return FeatureAccess.getIndividualAccountsLabel(currentUser);
    }
    if (ln === "property management operations") return "Staff accounts";
    if (ln === "property management host") return "Administrator accounts";
    return roleName;
  };

  const handleAddEmployeeInputChange = e => {
    const { name, value } = e.target;
    setAddEmployeeForm(prev => ({ ...prev, [name]: value }));
  };

  // Fetch single org data when accordion is expanded
  const fetchSingleOrgData = useCallback(async orgId => {
    try {
      const response = await organizationApiService.getOrganizationById(orgId);
      const orgData = response.data?.data || response.data;
      if (orgData) {
        setExpandedOrgData(prev => ({ ...prev, [orgId]: orgData }));
      }
    } catch (error) {
      logger.error("Error fetching organization details:", error);
    }
  }, []);

  const handleToggleAccordion = useCallback(
    companyId => {
      const isExpanding = openCompanyAccordion !== companyId;
      setOpenCompanyAccordion(isExpanding ? companyId : null);
      if (isExpanding && companyId) {
        fetchSingleOrgData(companyId);
      }
    },
    [openCompanyAccordion, setOpenCompanyAccordion, fetchSingleOrgData]
  );

  const handleAddEmployeeSubmit = async () =>
    runWithCustomerManagement(async () => {
      if (!selectedOrganizationForEmployee) return;

      // Find role ID from the selected role
      const role = organizationRoles.find(r => r.code === addEmployeeForm.role);
      const roleId = role?.id;

      if (!roleId) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Please select a valid role" },
            },
          },
          "validate role"
        );
        return;
      }

      if (addEmployeeForm.username && addEmployeeForm.username.trim() !== addEmployeeForm.username) {
        showApiMessage(
          {
            response: {
              status: 400,
              data: { message: "Username cannot have leading or trailing spaces" },
            },
          },
          "validate username"
        );
        return;
      }

      const employeeData = {
        username: addEmployeeForm.username,
        firstName: addEmployeeForm.firstName,
        lastName: addEmployeeForm.lastName,
        emailAddress: addEmployeeForm.emailAddress,
        msisdn: addEmployeeForm.msisdn,
      };

      const result = await addEmployeeToChildOrganization(selectedOrganizationForEmployee.id, employeeData, roleId);

      if (result?.success) {
        setShowAddEmployeeModal(false);
        setSelectedOrganizationForEmployee(null);
        setAddEmployeeForm({
          username: "",
          firstName: "",
          lastName: "",
          emailAddress: "",
          msisdn: "",
          role: "ROLE_EMPLOYEE",
        });
        // Refresh organizations to show updated employee count
        await fetchOrganizations();
      }

      return result;
    });

  // Handle organization creation with refresh
  const handleCreateOrganizationWithRefresh = async () =>
    runWithCustomerManagement(async () => {
      const result = await createOrganization();
      if (result?.success) {
        await fetchOrganizations(); // Refresh the organizations list
        setCreateOrgForm({}); // Reset the form
        setShowCreateOrganizationModal(false); // Close the create org modal
        setIsCreatingOrganization(false); // Reset to invite user mode
      }
      return result;
    });

  // Toggle status section accordion
  const toggleStatusSection = status => {
    setOpenStatusSections(prev => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  // Check if current user is HOST_PROPERTY_MANAGEMENT (only they can manage organizations)
  // Note: currentUser and isHostPropertyManagement already declared at the top of the component
  logger.log("Current user org type code:", orgTypeCode);
  logger.log("Is HOST_PROPERTY_MANAGEMENT:", isHostPropertyManagement);

  return (
    <>
      <HPageContainer>
        <HHeader />

        <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
          {/* Loading State - Full Page Shimmer */}
          {loading && (
            <div className="space-y-8 animate-pulse">
              {/* Company Management Header Shimmer */}
              <div className="flex items-center justify-between mb-8">
                <div className="h-8 w-48 bg-slate-200 rounded"></div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-32 bg-slate-200 rounded"></div>
                  <div className="h-8 w-8 bg-slate-200 rounded"></div>
                </div>
              </div>

              {/* Company Cards Shimmer */}
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-6 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                        <div>
                          <div className="h-6 w-48 bg-slate-200 rounded mb-2"></div>
                          <div className="h-4 w-64 bg-slate-200 rounded"></div>
                        </div>
                      </div>
                      <div className="h-8 w-8 bg-slate-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* User Management Header Shimmer */}
              <div className="flex items-center justify-between mb-8">
                <div className="h-8 w-40 bg-slate-200 rounded"></div>
                <div className="h-8 w-8 bg-slate-200 rounded"></div>
              </div>

              {/* Role Sections Shimmer */}
              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-6 bg-white rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                        <div>
                          <div className="h-6 w-32 bg-slate-200 rounded mb-2"></div>
                          <div className="h-4 w-48 bg-slate-200 rounded"></div>
                        </div>
                      </div>
                      <div className="h-8 w-8 bg-slate-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Global Error State - only show if ALL sections have errors */}
          {shouldShowGlobalError && (
            <GlobalErrorState
              onRetry={() => {
                fetchOrganizations();
                refreshAllRoleSections();
              }}
            />
          )}

          {/* Main Content - show when not loading and not showing global error */}
          {!loading && !shouldShowGlobalError && (
            <div className="space-y-8">
              {!hasCustomerManagement && FeatureAccess.canUpgradePlan(currentUser) && (
                <div className="border border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm font-medium sm:text-base">Customer management is available on higher plans.</p>
                  <HUpgradeButton onClick={goToUpgrade} />
                </div>
              )}

              {/* Company Management Section - Use centralized access control */}
              {canManageOrganizations && (
                <>
                  {/* Company Management Header */}
                  <div
                    className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4"
                    data-tour="clients-management"
                  >
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Company Management</h1>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <HActionButton
                        label="Add Organization"
                        icon={<Building2 size={18} />}
                        onClick={() =>
                          runWithCustomerManagement(() => {
                            setSelectedRole(null);
                            setShowCreateOrganizationModal(true);
                          })
                        }
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 text-sm sm:text-base"
                      />
                      <button
                        onClick={() => setCompanyManagementOpen(!companyManagementOpen)}
                        className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Company Management Sections */}
                  {companyManagementOpen && (
                    <div className="space-y-8">
                      {hasOrganizationError ? (
                        <ErrorState
                          title="Failed to load organizations"
                          message={error}
                          onRetry={() => fetchOrganizations()}
                          retryLabel="Retry loading organizations"
                          className="mb-6"
                        />
                      ) : (
                        <>
                          {/* Active Organizations */}
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <h2 className="text-lg font-semibold text-gray-900">Active Organizations</h2>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {getFilteredCompanies(organizationsByStatus.ACTIVE).length}
                                </span>
                              </div>
                              <button
                                onClick={() => toggleStatusSection("ACTIVE")}
                                className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                              >
                                <ChevronUp
                                  className={`w-4 h-4 transition-transform ${openStatusSections.ACTIVE ? "rotate-0" : "rotate-180"}`}
                                />
                              </button>
                            </div>
                            {openStatusSections.ACTIVE && (
                              <>
                                {organizationsByStatus.ACTIVE?.length > 0 ? (
                                  <CompanyAccordion
                                    companies={getFilteredCompanies(organizationsByStatus.ACTIVE)}
                                    openCompanyAccordion={openCompanyAccordion}
                                    onToggleAccordion={handleToggleAccordion}
                                    expandedOrgData={expandedOrgData}
                                    onEditOrganization={handleEditOrganizationGuarded}
                                    onDeleteOrganization={handleDeleteOrganizationGuarded}
                                  />
                                ) : (
                                  <div className="py-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                                    <Building2 className="mx-auto w-8 h-8 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No active organizations</h3>
                                    <p className="mt-1 text-sm text-gray-500">Active organizations will appear here.</p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Inactive Organizations */}
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <h2 className="text-lg font-semibold text-gray-900">Inactive Organizations</h2>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {getFilteredCompanies(organizationsByStatus.INACTIVE).length}
                                </span>
                              </div>
                              <button
                                onClick={() => toggleStatusSection("INACTIVE")}
                                className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                              >
                                <ChevronUp
                                  className={`w-4 h-4 transition-transform ${openStatusSections.INACTIVE ? "rotate-0" : "rotate-180"}`}
                                />
                              </button>
                            </div>
                            {openStatusSections.INACTIVE && (
                              <>
                                {organizationsByStatus.INACTIVE?.length > 0 ? (
                                  <CompanyAccordion
                                    companies={getFilteredCompanies(organizationsByStatus.INACTIVE)}
                                    openCompanyAccordion={openCompanyAccordion}
                                    onToggleAccordion={handleToggleAccordion}
                                    expandedOrgData={expandedOrgData}
                                    onEditOrganization={handleEditOrganizationGuarded}
                                    onDeleteOrganization={handleDeleteOrganizationGuarded}
                                  />
                                ) : (
                                  <div className="py-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                                    <Building2 className="mx-auto w-8 h-8 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                                      No inactive organizations
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                      Inactive organizations will appear here.
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Disabled Organizations */}
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <h2 className="text-lg font-semibold text-gray-900">Disabled Organizations</h2>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  {getFilteredCompanies(organizationsByStatus.DISABLED).length}
                                </span>
                              </div>
                              <button
                                onClick={() => toggleStatusSection("DISABLED")}
                                className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                              >
                                <ChevronUp
                                  className={`w-4 h-4 transition-transform ${openStatusSections.DISABLED ? "rotate-0" : "rotate-180"}`}
                                />
                              </button>
                            </div>
                            {openStatusSections.DISABLED && (
                              <>
                                {organizationsByStatus.DISABLED?.length > 0 ? (
                                  <CompanyAccordion
                                    companies={getFilteredCompanies(organizationsByStatus.DISABLED)}
                                    openCompanyAccordion={openCompanyAccordion}
                                    onToggleAccordion={handleToggleAccordion}
                                    expandedOrgData={expandedOrgData}
                                    onEditOrganization={handleEditOrganizationGuarded}
                                    onDeleteOrganization={handleDeleteOrganizationGuarded}
                                  />
                                ) : (
                                  <div className="py-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                                    <Building2 className="mx-auto w-8 h-8 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                                      No disabled organizations
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                      Disabled organizations will appear here.
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Show create button if no organizations at all */}
                          {organizationsByStatus.ACTIVE?.length === 0 &&
                            organizationsByStatus.INACTIVE?.length === 0 &&
                            organizationsByStatus.DISABLED?.length === 0 && (
                              <div className="py-12 text-center">
                                <Building2 className="mx-auto w-12 h-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No organizations found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                  Get started by creating a new client organization.
                                </p>
                                <HActionButton
                                  label="Add Organization"
                                  icon={<Building2 size={18} />}
                                  onClick={() =>
                                    runWithCustomerManagement(() => {
                                      setSelectedRole(null);
                                      setShowCreateOrganizationModal(true);
                                    })
                                  }
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 mt-4"
                                />
                              </div>
                            )}

                          {/* Organization Pagination */}
                          {organizations.length > 0 && pagination.totalCount > pagination.limit && (
                            <div className="flex justify-center pt-4 mt-6 px-4 sm:px-0">
                              <Pagination
                                className="pagination-bar"
                                currentPage={pagination.page}
                                totalCount={pagination.totalCount}
                                pageSize={pagination.limit}
                                onPageChange={handleOrganizationPageChange}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* User Management Header */}
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setUserManagementOpen(!userManagementOpen)}
                    className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* User Management Sections */}
              {userManagementOpen && (
                <div className="space-y-6">
                  {hasRoleSectionsError ? (
                    <div className="p-6 bg-white rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                            <UserPlus className="w-6 h-6 text-gray-600" />
                          </div>
                          <div>
                            <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                              <span>0 users</span>
                            </div>
                          </div>
                        </div>

                        <ErrorState
                          title={
                            hasOrganizationError
                              ? "Failed to load organizations and user roles"
                              : "Failed to load user roles"
                          }
                          message={
                            hasOrganizationError
                              ? "Unable to load organizations, which prevents user management from working. This may be due to a network issue or insufficient permissions."
                              : "Unable to load user management roles. This may be due to a network issue or insufficient permissions."
                          }
                          onRetry={() => {
                            // Retry loading organizations and roles
                            fetchOrganizations();
                            fetchOrganizationRoles();
                            fetchOrganizationTypes();
                          }}
                          retryLabel="Retry loading data"
                        />
                      </div>
                    </div>
                  ) : availableRoles.length > 0 ? (
                    availableRoles.map((role, _index) => {
                      const section = roleSections[role.code];
                      if (!section) {
                        // If section doesn't exist, show loading state
                        return (
                          <div key={role.code} className="p-6 bg-white rounded-xl border border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                                  <UserPlus className="w-6 h-6 text-gray-600" />
                                </div>
                                <div>
                                  <h2 className="text-xl font-semibold text-gray-900">
                                    {getFriendlyRoleName(role.name)}
                                  </h2>
                                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                    <span>Loading...</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-center py-8">
                              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                              <span className="ml-2 text-gray-600">Loading users...</span>
                            </div>
                          </div>
                        );
                      }

                      // If this specific role section has an error, show error state
                      if (section.error) {
                        return (
                          <div key={role.code} className="p-6 bg-white rounded-xl border border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
                                  <UserPlus className="w-6 h-6 text-gray-600" />
                                </div>
                                <div>
                                  <h2 className="text-xl font-semibold text-gray-900">
                                    {getFriendlyRoleName(role.name)}
                                  </h2>
                                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                    <span>0 users</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <ErrorState
                              title={`Failed to load ${getFriendlyRoleName(role.name).toLowerCase()}`}
                              message={section.error}
                              onRetry={() => fetchRoleSectionUsers(role.code)}
                              retryLabel="Retry loading users"
                            />
                          </div>
                        );
                      }

                      return (
                        <RoleSectionTable
                          key={role.code}
                          role={role}
                          section={section}
                          onToggleSection={() => toggleRoleSection(role.code)}
                          onSearchChange={searchTerm => updateRoleSectionSearch(role.code, searchTerm)}
                          onStatusChange={status => {
                            updateRoleSectionStatus(role.code, status);
                            fetchRoleSectionUsers(role.code, status);
                          }}
                          onAddUser={() =>
                            runWithCustomerManagementIfNeeded(() => {
                              setSelectedRole(role);
                              setIsCreatingOrganization(false);
                              setShowInviteModal(true);
                            }, role)
                          }
                          isRoleSubscriberHostOperations={isRoleSubscriberHostOperations}
                          isHostPropertyManagement={isHostPropertyManagement}
                          onEditUserDetails={user => {
                            handleEditUserDetails(user);
                          }}
                          onDeleteUser={user => {
                            handleDeleteUser(user);
                          }}
                          onRetry={() => fetchRoleSectionUsers(role.code)}
                          userCounts={{
                            active: section.users.filter(u => u.status === "ACTIVE").length,
                            inactive: section.users.filter(u => u.status === "INACTIVE").length,
                            disabled: section.users.filter(u => u.status === "DISABLED").length,
                          }}
                        />
                      );
                    })
                  ) : (
                    <div className="py-12 text-center">
                      <UserPlus className="mx-auto w-12 h-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No user roles available</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        User management roles are being loaded. Please wait a moment.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <DeleteUserModal
          isOpen={showDeleteUserModal}
          onClose={() => setShowDeleteUserModal(false)}
          userToDelete={userToDelete}
          deleteUserLoading={deleteUserLoading}
          onConfirm={handleDeleteUserConfirmWithRefresh}
        />

        <EditUserRoleModal
          isOpen={showEditUserRoleModal}
          onClose={() => setShowEditUserRoleModal(false)}
          selectedUserForEdit={selectedUserForEdit}
          editUserRoleForm={editUserRoleForm}
          editUserRoleLoading={editUserRoleLoading}
          editUserError={editUserError}
          organizationRoles={organizationRoles}
          onInputChange={handleEditUserRoleInputChange}
          onSubmit={handleEditUserRoleSubmitWithRefresh}
        />

        {/* Edit User Details Modal */}
        <Dialog open={showEditUserDetailsModal} onOpenChange={setShowEditUserDetailsModal}>
          <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-lg border border-gray-200 p-0 mx-4 sm:mx-0">
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">Edit User Details</DialogTitle>
                <p className="text-sm text-gray-500">Update user information and settings</p>
              </div>

              {editUserError && (
                <div className="p-3 bg-red-50 rounded-md border border-red-200 mb-4">
                  <p className="text-sm text-red-600">{editUserError}</p>
                </div>
              )}

              <div className="space-y-6 max-h-[60vh] overflow-y-auto min-h-[400px]">
                {editUserDetailsLoading ? (
                  <div className="flex justify-center items-center h-full min-h-[300px]">
                    <HSpinner size="large" />
                  </div>
                ) : selectedUserForEdit ? (
                  <>
                    {/* Profile Picture */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Profile Picture</Label>
                      <div className="mt-2 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {editUserDetailsForm.imageUrl ? (
                            <img
                              src={editUserDetailsForm.imageUrl}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-10 h-10 text-gray-400" />
                          )}
                        </div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              try {
                                logger.log("Uploading profile picture:", file.name);
                                const response = await spaceManagementService.uploadMedia([file]);
                                logger.log("Upload response:", response);
                                if (
                                  (response.status === 200 || response.status === 201) &&
                                  response.data &&
                                  response.data.length > 0
                                ) {
                                  const imageUrl = response.data[0];
                                  logger.log("Extracted imageUrl:", imageUrl);
                                  setEditUserDetailsForm(prev => ({ ...prev, imageUrl }));
                                  toastUtils.success("Profile picture uploaded successfully");
                                } else {
                                  logger.error("Invalid upload response:", response);
                                  toastUtils.error("Failed to get image URL from upload");
                                }
                              } catch (error) {
                                logger.error("Error uploading profile picture:", error);
                                toastUtils.error("Failed to upload image: " + (error.message || "Unknown error"));
                              }
                            }
                          }}
                          disabled={editUserDetailsLoading}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="edit-username" className="text-sm font-medium text-gray-700">
                        Username
                      </Label>
                      <Input
                        id="edit-username"
                        type="text"
                        name="username"
                        value={editUserDetailsForm.username || selectedUserForEdit.username || ""}
                        disabled={true}
                        placeholder="Username cannot be changed"
                        className="bg-gray-50 cursor-not-allowed"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="edit-firstName" className="text-sm font-medium text-gray-700">
                          First Name *
                        </Label>
                        <Input
                          id="edit-firstName"
                          type="text"
                          name="firstName"
                          value={editUserDetailsForm.firstName}
                          onChange={handleEditUserDetailsInputChange}
                          disabled={editUserDetailsLoading}
                          placeholder="First name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-lastName" className="text-sm font-medium text-gray-700">
                          Last Name *
                        </Label>
                        <Input
                          id="edit-lastName"
                          type="text"
                          name="lastName"
                          value={editUserDetailsForm.lastName}
                          onChange={handleEditUserDetailsInputChange}
                          disabled={editUserDetailsLoading}
                          placeholder="Last name"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="edit-email" className="text-sm font-medium text-gray-700">
                        Email Address *
                      </Label>
                      <Input
                        id="edit-email"
                        type="email"
                        name="emailAddress"
                        value={editUserDetailsForm.emailAddress}
                        onChange={handleEditUserDetailsInputChange}
                        disabled={editUserDetailsLoading}
                        placeholder="user@example.com"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="edit-phone" className="text-sm font-medium text-gray-700">
                        Phone Number
                      </Label>
                      <Input
                        id="edit-phone"
                        type="text"
                        name="msisdn"
                        value={editUserDetailsForm.msisdn || ""}
                        onChange={handleEditUserDetailsInputChange}
                        disabled={editUserDetailsLoading}
                        placeholder="+254712345678"
                      />
                    </div>

                    {/* Organization Role */}
                    <div>
                      <Label htmlFor="edit-role" className="text-sm font-medium text-gray-700">
                        Organization Role
                      </Label>
                      <Select
                        value={editUserDetailsForm.organizationRoleId || selectedUserForEdit.organizationRoleId}
                        onValueChange={value =>
                          setEditUserDetailsForm(prev => ({ ...prev, organizationRoleId: value }))
                        }
                        disabled={editUserDetailsLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizationRoles
                            .filter(
                              role =>
                                role.organizationTypeId ===
                                (selectedUserForEdit.organization?.typeId || selectedUserForEdit.organization?.type?.id)
                            )
                            .map(role => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status Section */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Account Status</Label>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-2">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(selectedUserForEdit.status)}>
                            {selectedUserForEdit.status}
                          </Badge>
                          {selectedUserForEdit.status === "ACTIVE" ? (
                            <span className="text-xs text-gray-500">Account is active and functional</span>
                          ) : selectedUserForEdit.status === "DISABLED" ? (
                            <span className="text-xs text-gray-500">Account has been disabled by an admin</span>
                          ) : (
                            <span className="text-xs text-gray-500">Account is pending confirmation</span>
                          )}
                        </div>

                        {!selectedUserForEdit.isOwner && (
                          <div className="flex gap-2">
                            {selectedUserForEdit.status === "INACTIVE" ? (
                              <button
                                type="button"
                                onClick={handleResendActivationEmail}
                                disabled={editUserDetailsLoading}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                              >
                                {editUserDetailsLoading ? (
                                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                                ) : (
                                  <>
                                    <UserPlus size={14} className="mr-1.5" />
                                    Resend Activation
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={async () => {
                                  const result = await handleToggleUserStatus(selectedUserForEdit);
                                  if (result?.success) {
                                    setShowEditUserDetailsModal(false);
                                    handleUserOperationSuccess();
                                  }
                                }}
                                disabled={editUserDetailsLoading}
                                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                  selectedUserForEdit.status === "ACTIVE"
                                    ? "text-red-600 border-red-200 hover:bg-red-50 bg-white"
                                    : "text-green-600 border-green-200 hover:bg-green-50 bg-white"
                                }`}
                              >
                                {editUserDetailsLoading ? (
                                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                                ) : selectedUserForEdit.status === "ACTIVE" ? (
                                  <>
                                    <UserX size={14} className="mr-1.5" />
                                    Deactivate Account
                                  </>
                                ) : (
                                  <>
                                    <UserCheck size={14} className="mr-1.5" />
                                    Reactivate Account
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <HActionButton
                  label="Cancel"
                  icon={<X size={16} />}
                  variant="secondary"
                  onClick={() => setShowEditUserDetailsModal(false)}
                  fullWidth
                />
                <HActionButton
                  label={editUserDetailsLoading ? "Updating..." : "Update Details"}
                  icon={editUserDetailsLoading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  variant="primary"
                  onClick={handleEditUserDetailsSubmitWithRefresh}
                  fullWidth
                  disabled={editUserDetailsLoading}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite User Modal */}
        <Dialog
          open={showInviteModal}
          onOpenChange={open => {
            setShowInviteModal(open);
            if (!open) {
              setSelectedRole(null);
            }
          }}
        >
          <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-lg border border-gray-200 p-0 mx-4 sm:mx-0">
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedRole?.name ? `Add ${getFriendlyRoleName(selectedRole.name)}` : "Add account"}
                </DialogTitle>
                <p className="text-sm text-gray-500">Create a new user account with the selected role</p>
              </div>
              <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                <div>
                  <Label htmlFor="invite-username" className="text-sm font-medium text-gray-700">
                    Username
                  </Label>
                  <div className="relative">
                    <Input
                      id="invite-username"
                      type="text"
                      name="username"
                      value={inviteUserForm.username || ""}
                      onChange={handleInviteUserInputChange}
                      className={`pr-10 ${
                        usernameError
                          ? "border-red-300 focus:ring-red-500 bg-red-50"
                          : usernameAvailable === true
                            ? "border-green-300 focus:ring-green-500 bg-green-50"
                            : ""
                      }`}
                      placeholder="Enter username"
                      required
                    />
                    {isCheckingUsername && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {!isCheckingUsername && usernameAvailable === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                    {!isCheckingUsername && usernameAvailable === false && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  {usernameError && <p className="mt-1 text-sm text-red-600 font-medium">{usernameError}</p>}
                  {!usernameError && usernameAvailable === true && (
                    <p className="mt-1 text-sm text-green-600 font-medium">Username is available!</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="invite-firstName" className="text-sm font-medium text-gray-700">
                      First Name
                    </Label>
                    <Input
                      id="invite-firstName"
                      type="text"
                      name="firstName"
                      value={inviteUserForm.firstName || ""}
                      onChange={handleInviteUserInputChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite-lastName" className="text-sm font-medium text-gray-700">
                      Last Name
                    </Label>
                    <Input
                      id="invite-lastName"
                      type="text"
                      name="lastName"
                      value={inviteUserForm.lastName || ""}
                      onChange={handleInviteUserInputChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="invite-email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="invite-email"
                    type="email"
                    name="emailAddress"
                    value={inviteUserForm.emailAddress || ""}
                    onChange={handleInviteUserInputChange}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1">Role</Label>
                  {selectedRole ? (
                    <div className="w-full p-3 border border-gray-200 bg-gray-50 rounded-md text-sm">
                      {getFriendlyRoleName(selectedRole.name)}
                      <p className="mt-1 text-xs text-gray-500">
                        Role automatically selected from {getFriendlyRoleName(selectedRole.name)} section
                      </p>
                    </div>
                  ) : (
                    <div className="w-full p-3 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-500">
                      <p className="text-gray-500">Please select a role from a role section</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <HActionButton
                  label="Cancel"
                  icon={<X size={16} />}
                  variant="secondary"
                  onClick={() => setShowInviteModal(false)}
                  fullWidth
                />
                <HActionButton
                  label={isBookingLoading ? "Adding..." : "Create Account"}
                  icon={isBookingLoading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                  variant="primary"
                  onClick={handleInviteUserWithRefresh}
                  fullWidth
                  disabled={isBookingLoading || isCheckingUsername || usernameAvailable === false}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Organization Modal */}
        <EditOrganizationModal
          isOpen={showEditOrganizationModal}
          onClose={() => setShowEditOrganizationModal(false)}
          selectedOrganization={selectedOrganization}
          editOrganizationForm={editOrganizationForm}
          editOrganizationLoading={editOrganizationLoading}
          editOrganizationError={editOrganizationError}
          fetchingOrganizationData={fetchingOrganizationData}
          onInputChange={handleEditOrganizationInputChange}
          onSubmit={async () =>
            runWithCustomerManagement(async () => {
              const result = await handleEditOrganizationSubmit();
              if (result?.success) {
                await fetchOrganizations();
              }
              return result;
            })
          }
        />

        {/* Delete Organization Modal */}
        <DeleteOrganizationModal
          isOpen={showDeleteOrganizationModal}
          onClose={() => setShowDeleteOrganizationModal(false)}
          organizationToDelete={organizationToDelete}
          deleteOrganizationLoading={deleteOrganizationLoading}
          onConfirm={async () =>
            runWithCustomerManagement(async () => {
              const result = await handleDeleteOrganizationConfirm();
              if (result?.success) {
                await fetchOrganizations();
              }
              return result;
            })
          }
        />

        {/* Add Employee Modal */}
        <Dialog
          open={showAddEmployeeModal}
          onOpenChange={open => {
            setShowAddEmployeeModal(open);
            if (!open) {
              setSelectedOrganizationForEmployee(null);
              setAddEmployeeForm({
                username: "",
                firstName: "",
                lastName: "",
                emailAddress: "",
                msisdn: "",
                role: "ROLE_EMPLOYEE",
              });
            }
          }}
        >
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto bg-white rounded-lg border border-gray-200 p-0">
            <div className="p-6">
              <div className="mb-6">
                <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">
                  Add Employee to {selectedOrganizationForEmployee?.name}
                </DialogTitle>
                <p className="text-sm text-gray-500">Add a new employee to this organization</p>
              </div>

              <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                <div>
                  <Label htmlFor="add-username" className="text-sm font-medium text-gray-700">
                    Username *
                  </Label>
                  <div className="relative">
                    <Input
                      id="add-username"
                      type="text"
                      name="username"
                      value={addEmployeeForm.username}
                      onChange={handleAddEmployeeInputChange}
                      className={`pr-10 ${
                        addEmployeeUsernameError
                          ? "border-red-300 focus:ring-red-500 bg-red-50"
                          : addEmployeeUsernameAvailable === true
                            ? "border-green-300 focus:ring-green-500 bg-green-50"
                            : ""
                      }`}
                      placeholder="Enter username"
                      required
                    />
                    {isCheckingAddEmployeeUsername && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {!isCheckingAddEmployeeUsername && addEmployeeUsernameAvailable === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                    {!isCheckingAddEmployeeUsername && addEmployeeUsernameAvailable === false && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  {addEmployeeUsernameError && <p className="mt-1 text-xs text-red-600">{addEmployeeUsernameError}</p>}
                  {!addEmployeeUsernameError && addEmployeeUsernameAvailable === true && (
                    <p className="mt-1 text-xs text-green-600">Username is available</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="add-firstName" className="text-sm font-medium text-gray-700">
                      First Name *
                    </Label>
                    <Input
                      id="add-firstName"
                      type="text"
                      name="firstName"
                      value={addEmployeeForm.firstName}
                      onChange={handleAddEmployeeInputChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="add-lastName" className="text-sm font-medium text-gray-700">
                      Last Name *
                    </Label>
                    <Input
                      id="add-lastName"
                      type="text"
                      name="lastName"
                      value={addEmployeeForm.lastName}
                      onChange={handleAddEmployeeInputChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="add-email" className="text-sm font-medium text-gray-700">
                    Email Address *
                  </Label>
                  <Input
                    id="add-email"
                    type="email"
                    name="emailAddress"
                    value={addEmployeeForm.emailAddress}
                    onChange={handleAddEmployeeInputChange}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="add-phone" className="text-sm font-medium text-gray-700">
                    Phone Number
                  </Label>
                  <Input
                    id="add-phone"
                    type="text"
                    name="msisdn"
                    value={addEmployeeForm.msisdn}
                    onChange={handleAddEmployeeInputChange}
                    placeholder="+254712345678"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1">Role</Label>
                  <div className="w-full p-3 border border-gray-200 bg-gray-50 rounded-md text-sm">
                    Employee
                    <p className="mt-1 text-xs text-gray-500">Role automatically assigned for new employees</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <HActionButton
                  label="Cancel"
                  icon={<X size={16} />}
                  variant="secondary"
                  onClick={() => setShowAddEmployeeModal(false)}
                  fullWidth
                />
                <HActionButton
                  label={isBookingLoading ? "Adding..." : "Add Employee"}
                  icon={isBookingLoading ? <Loader2 size={16} className="animate-spin" /> : <User size={16} />}
                  variant="primary"
                  onClick={handleAddEmployeeSubmit}
                  fullWidth
                  disabled={
                    isBookingLoading ||
                    !addEmployeeForm.username ||
                    !addEmployeeForm.firstName ||
                    !addEmployeeForm.lastName ||
                    !addEmployeeForm.emailAddress
                  }
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Organization Modal */}
        <Dialog
          open={showCreateOrganizationModal}
          onOpenChange={open => {
            setShowCreateOrganizationModal(open);
            if (!open) {
              setCreateOrgForm({});
            }
          }}
        >
          <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-lg border border-gray-200 p-0 mx-4 sm:mx-0">
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <DialogTitle className="text-xl font-semibold text-gray-900">Create new organization</DialogTitle>
                  {createOrgForm?.spaceType && (
                    <HBadge1 tone="purple" size="sm" className="whitespace-nowrap">
                      {createOrgForm.spaceType?.toString().toUpperCase()}
                    </HBadge1>
                  )}
                </div>
                <p className="text-sm text-gray-500">Configure basic details, contacts, location and admin profile.</p>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Organization Name - Full Width */}
                <div>
                  <Label htmlFor="org-name" className="text-sm font-medium text-gray-700">
                    Organization Name *
                  </Label>
                  <div className="relative">
                    <Input
                      id="org-name"
                      type="text"
                      name="name"
                      value={createOrgForm.name || ""}
                      onChange={handleCreateOrgInputChange}
                      className={`pr-10 ${
                        organizationNameError
                          ? "border-red-500 focus:ring-red-500"
                          : organizationNameAvailable === true
                            ? "border-green-500 focus:ring-green-500"
                            : ""
                      }`}
                      placeholder="Enter organization name"
                      required
                    />
                    {/* Loading/Status indicator */}
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {isCheckingOrganization && createOrgForm.name ? (
                        <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : organizationNameAvailable === true ? (
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : organizationNameAvailable === false ? (
                        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </div>
                  </div>
                  {organizationNameError ? (
                    <div className="mt-1 text-sm text-red-500">{organizationNameError}</div>
                  ) : organizationNameAvailable === true ? (
                    <div className="mt-1 text-sm text-green-600">Organization name is available!</div>
                  ) : null}
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="contact-email" className="text-sm font-medium text-gray-700">
                      Contact Email *
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      name="contactEmail"
                      value={createOrgForm.contactEmail || ""}
                      onChange={handleCreateOrgInputChange}
                      placeholder="contact@organization.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="alternate-email" className="text-sm font-medium text-gray-700">
                      Alternate Email
                    </Label>
                    <Input
                      id="alternate-email"
                      type="email"
                      name="alternateEmail"
                      value={createOrgForm.alternateEmail || ""}
                      onChange={handleCreateOrgInputChange}
                      placeholder="alternate@organization.com"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact-phone" className="text-sm font-medium text-gray-700">
                    Phone Number
                  </Label>
                  <Input
                    id="contact-phone"
                    type="text"
                    name="contactMsisdn"
                    value={createOrgForm.contactMsisdn || ""}
                    onChange={handleCreateOrgInputChange}
                    placeholder="+254712345678"
                  />
                </div>

                {/* Organization Settings */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="country" className="text-sm font-medium text-gray-700">
                      Country *
                    </Label>
                    <Select
                      value={createOrgForm.countryId ? createOrgForm.countryId.toString() : ""}
                      onValueChange={value =>
                        handleCreateOrgInputChange({ target: { name: "countryId", value: parseInt(value, 10) } })
                      }
                      disabled={isBookingLoading || isLoadingCountries}
                    >
                      <SelectTrigger
                        id="country"
                        className={`w-full ${!createOrgForm.countryId ? "text-muted-foreground" : ""}`}
                      >
                        <SelectValue placeholder={isLoadingCountries ? "Loading countries..." : "Select Country"} />
                      </SelectTrigger>
                      <SelectContent>
                        {countries?.map(country => (
                          <SelectItem key={country.id} value={country.id.toString()}>
                            {country.name || country.officialName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="space-type" className="text-sm font-medium text-gray-700">
                      Space Type *
                    </Label>
                    <select
                      id="space-type"
                      name="spaceType"
                      value={createOrgForm.spaceType || ""}
                      onChange={handleCreateOrgInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    >
                      <option value="">Select Space Type</option>
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="max-users" className="text-sm font-medium text-gray-700">
                    Max Users *
                  </Label>
                  <Input
                    id="max-users"
                    type="number"
                    name="config.maxUsers"
                    value={createOrgForm.config?.maxUsers || ""}
                    onChange={handleCreateOrgInputChange}
                    min="0"
                    placeholder="100"
                    required
                  />
                </div>

                {/* Admin Profile */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="admin-firstName" className="text-sm font-medium text-gray-700">
                        Admin First Name *
                      </Label>
                      <Input
                        id="admin-firstName"
                        type="text"
                        name="adminProfile.firstName"
                        value={createOrgForm.adminProfile?.firstName || ""}
                        onChange={handleCreateOrgInputChange}
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin-lastName" className="text-sm font-medium text-gray-700">
                        Admin Last Name *
                      </Label>
                      <Input
                        id="admin-lastName"
                        type="text"
                        name="adminProfile.lastName"
                        value={createOrgForm.adminProfile?.lastName || ""}
                        onChange={handleCreateOrgInputChange}
                        placeholder="Last name"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="admin-email" className="text-sm font-medium text-gray-700">
                        Admin Email *
                      </Label>
                      <Input
                        id="admin-email"
                        type="email"
                        name="adminProfile.emailAddress"
                        value={createOrgForm.adminProfile?.emailAddress || ""}
                        onChange={handleCreateOrgInputChange}
                        placeholder="admin@organization.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin-username" className="text-sm font-medium text-gray-700">
                        Admin Username *
                      </Label>
                      <div className="relative">
                        <Input
                          id="admin-username"
                          type="text"
                          name="adminProfile.username"
                          value={createOrgForm.adminProfile?.username || ""}
                          onChange={handleCreateOrgInputChange}
                          className={`pr-10 ${
                            orgUsernameError
                              ? "border-red-300 focus:ring-red-500 bg-red-50"
                              : orgUsernameAvailable === true
                                ? "border-green-300 focus:ring-green-500 bg-green-50"
                                : ""
                          }`}
                          placeholder="admin_username"
                          required
                        />
                        {isCheckingOrgUsername && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        {!isCheckingOrgUsername && orgUsernameAvailable === true && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                        {!isCheckingOrgUsername && orgUsernameAvailable === false && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      {orgUsernameError && <p className="mt-1 text-xs text-red-600">{orgUsernameError}</p>}
                      {!orgUsernameError && orgUsernameAvailable === true && (
                        <p className="mt-1 text-xs text-green-600">Username is available</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label htmlFor="admin-phone" className="text-sm font-medium text-gray-700">
                      Admin Phone Number *
                    </Label>
                    <Input
                      id="admin-phone"
                      type="text"
                      name="adminProfile.msisdn"
                      value={createOrgForm.adminProfile?.msisdn || ""}
                      onChange={handleCreateOrgInputChange}
                      placeholder="+254712345678"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <HActionButton
                  label="Cancel"
                  icon={<X size={16} />}
                  variant="secondary"
                  onClick={() => setShowCreateOrganizationModal(false)}
                  fullWidth
                />
                <HActionButton
                  label={isBookingLoading ? "Creating Organization..." : "Create Organization"}
                  icon={isBookingLoading ? <Loader2 size={16} className="animate-spin" /> : <Building2 size={16} />}
                  variant="primary"
                  onClick={handleCreateOrganizationWithRefresh}
                  fullWidth
                  disabled={isBookingLoading}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </HPageContainer>
    </>
  );
};

export default HostClients;
